import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anon
  const [error, setError] = useState(null);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("autexio_token");
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("autexio_token");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("autexio_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const register = useCallback(async (payload) => {
    // payload: { name, email, password, company_name?, phone? }
    // The backend ignores any role/dealer_id sent from clients (T0 guard).
    setError(null);
    try {
      const { data } = await api.post("/auth/register", payload);
      localStorage.setItem("autexio_token", data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("autexio_token");
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, error, login, register, logout, reload: loadMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
